import { flashToast } from "./dom";
import { iconSvg } from "./icons";
import { t } from "./i18n";
import { reducedMotion } from "./motion_pref";
import { buzz } from "./tweens";

// a board of columns with cards you drag, hold and drag on touch, or move with a picker. the tasks and the commissions boards both use it.
// the owner hands in the columns, the cards, how to build one and what a move or a trash does. everything that moves is here

export interface KanbanColumn {
  id: string;
  label: string;
  // a css colour, otherwise the column takes the colour its status-<id> class gives it
  color?: string;
}

export interface KanbanCard<T> {
  key: string;
  column: string;
  data: T;
}

// "arm" asks for a second tap on the card's can first, "now" trashes at once, "none" has no trash for this card
export type TrashMode = "arm" | "now" | "none";

export interface KanbanOptions<T> {
  host: HTMLElement;
  columns: KanbanColumn[];
  // build the card element (an li). the board adds the key and the drag
  build: (card: KanbanCard<T>, column: KanbanColumn) => HTMLElement;
  move: (card: KanbanCard<T>, to: string) => void;
  trash: (card: KanbanCard<T>, tile: HTMLElement) => void;
  trashMode?: (card: KanbanCard<T>) => TrashMode;
  // read only boards (someone else's) cannot be dragged
  canMove?: (card: KanbanCard<T>) => boolean;
  emptyText?: string;
}

export interface Kanban<T> {
  paint: (cards: Array<KanbanCard<T>>) => void;
  dragging: () => boolean;
}

export function calmMotion(): boolean {
  return reducedMotion();
}

// the move picker: a bottom sheet on phones, a centred card on wide screens, one 44pt option per column
export function openColumnPicker(title: string, columns: KanbanColumn[], current: string, pick: (id: string) => void): void {
  const modal = document.createElement("div");
  modal.className = "modal task-picker";
  const sheet = document.createElement("div");
  sheet.className = "modal-sheet panel";
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-label", t("move to"));
  const heading = document.createElement("h3");
  heading.className = "task-picker-title";
  heading.textContent = title;
  const list = document.createElement("div");
  list.className = "task-picker-list";
  const close = () => modal.remove();
  for (const column of columns) {
    const option = document.createElement("button");
    option.type = "button";
    option.className = `task-picker-option status-${column.id}${column.id === current ? " current" : ""}`;
    if (column.color) option.style.setProperty("--st", column.color);
    option.setAttribute("aria-pressed", String(column.id === current));
    const dot = document.createElement("i");
    dot.className = "task-dot";
    const label = document.createElement("span");
    label.textContent = column.label;
    option.append(dot, label);
    option.addEventListener("click", () => {
      close();
      if (column.id !== current) pick(column.id);
    });
    list.append(option);
  }
  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "pill task-picker-cancel";
  cancel.textContent = t("cancel");
  cancel.addEventListener("click", close);
  sheet.append(heading, list, cancel);
  modal.append(sheet);
  modal.addEventListener("click", (event) => {
    if (event.target === modal) close();
  });
  modal.addEventListener("keydown", (event) => {
    if (event.key === "Escape") close();
  });
  document.body.append(modal);
  (list.querySelector(".current") as HTMLElement | null ?? list.firstElementChild as HTMLElement | null)?.focus();
}

// the crunch a card makes on its way to the bin, resolves once it has played
export async function crunchCard(card: HTMLElement): Promise<void> {
  if (calmMotion() || !card.isConnected) return;
  card.classList.add("task-crunch");
  await new Promise((resolve) => window.setTimeout(resolve, 240));
}

// the can on a card: two taps when `confirm` (the first arms it for a moment), the second runs it
const ARM_MS = 3500;
export function buildArmedTrash(label: string, confirm: boolean, run: () => void): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "pill icon-only task-trash";
  button.innerHTML = `${iconSvg("trash", 20)}<span class="task-trash-sure"></span>`;
  button.querySelector(".task-trash-sure")!.textContent = t("sure?");
  button.setAttribute("aria-label", label);
  let timer = 0;
  const disarm = () => {
    window.clearTimeout(timer);
    button.classList.remove("armed");
  };
  const arm = () => {
    button.classList.add("armed");
    window.clearTimeout(timer);
    timer = window.setTimeout(disarm, ARM_MS);
  };
  button.addEventListener("click", (event) => {
    event.stopPropagation();
    if (confirm && !button.classList.contains("armed")) {
      arm();
      return;
    }
    disarm();
    run();
  });
  // dropping a card on the trash asks for the same second tap
  button.addEventListener("task-arm", arm);
  return button;
}

export function createKanban<T>(options: KanbanOptions<T>): Kanban<T> {
  const { host, columns } = options;
  const columnIds = new Set(columns.map((column) => column.id));
  const trashMode = (card: KanbanCard<T>): TrashMode => options.trashMode?.(card) ?? "now";
  const canMove = (card: KanbanCard<T>): boolean => options.canMove?.(card) ?? true;
  // a board that is being dragged on is not rebuilt under the finger
  let isDragging = false;
  let pending: Array<KanbanCard<T>> | null = null;
  // where each card was, so the next paint can slide it to its new spot
  let dropRects = new Map<string, DOMRect>();
  let lastColumn = new Map<string, string>();
  let lastCounts = new Map<string, number>();
  let suppressClick = -1000;

  const measure = (): Map<string, DOMRect> => {
    const rects = new Map<string, DOMRect>();
    host.querySelectorAll<HTMLElement>("[data-key]").forEach((card) => rects.set(card.dataset.key!, card.getBoundingClientRect()));
    return rects;
  };

  const settle = () => {
    isDragging = false;
    if (pending) {
      const cards = pending;
      pending = null;
      paint(cards);
    }
  };

  function paint(cards: Array<KanbanCard<T>>): void {
    if (isDragging) {
      pending = cards;
      return;
    }
    const before = measure();
    for (const [key, rect] of dropRects) before.set(key, rect);
    dropRects = new Map();

    const byColumn = new Map<string, Array<KanbanCard<T>>>(columns.map((column) => [column.id, []]));
    for (const card of cards) byColumn.get(columnIds.has(card.column) ? card.column : columns[0].id)!.push(card);

    // keep a phone's sideways scroll where it was
    const scrolled = host.scrollLeft;
    const nextColumn = new Map<string, string>();
    const nextCounts = new Map<string, number>();
    const popped: HTMLElement[] = [];

    const built = columns.map((column) => {
      const list = byColumn.get(column.id)!;
      const section = document.createElement("section");
      section.className = `task-col status-${column.id}`;
      if (column.color) section.style.setProperty("--st", column.color);
      section.dataset.status = column.id;
      section.setAttribute("aria-label", column.label);
      const head = document.createElement("header");
      head.className = "task-col-head";
      const dot = document.createElement("i");
      dot.className = "task-dot";
      const name = document.createElement("h3");
      name.textContent = column.label;
      const count = document.createElement("b");
      count.className = "task-count";
      count.textContent = String(list.length);
      if (lastCounts.has(column.id) && lastCounts.get(column.id) !== list.length) popped.push(count);
      nextCounts.set(column.id, list.length);
      head.append(dot, name, count);
      const body = document.createElement("ul");
      body.className = "task-col-body";
      for (const card of list) {
        nextColumn.set(card.key, column.id);
        const tile = options.build(card, column);
        tile.dataset.key = card.key;
        if (canMove(card)) attachDrag(tile, card, column);
        body.append(tile);
      }
      if (!list.length) {
        const empty = document.createElement("li");
        empty.className = "task-empty";
        empty.textContent = options.emptyText ?? t("nothing here");
        body.append(empty);
      }
      section.append(head, body);
      return section;
    });

    const trash = document.createElement("section");
    trash.className = "task-col task-trash-col";
    trash.dataset.status = "trash";
    trash.setAttribute("aria-label", t("trash"));
    trash.innerHTML = `${iconSvg("trash", 28)}<span></span>`;
    trash.querySelector("span")!.textContent = t("trash");

    host.replaceChildren(...built, trash);
    host.scrollLeft = scrolled;

    // slide every card from where it was to where it is, landed ones settle with a little glow
    if (!calmMotion()) {
      host.querySelectorAll<HTMLElement>(".task-tile").forEach((tile) => {
        const key = tile.dataset.key!;
        const from = before.get(key);
        if (!from) return;
        const to = tile.getBoundingClientRect();
        const dx = from.left - to.left;
        const dy = from.top - to.top;
        if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
        tile.style.transition = "none";
        tile.style.transform = `translate(${dx}px, ${dy}px)`;
        void tile.offsetWidth;
        tile.classList.add("flip");
        tile.style.transition = "";
        tile.style.transform = "";
        if (lastColumn.has(key) && lastColumn.get(key) !== nextColumn.get(key)) tile.classList.add("landed");
        tile.addEventListener("transitionend", () => tile.classList.remove("flip"), { once: true });
        window.setTimeout(() => tile.classList.remove("flip", "landed"), 700);
      });
      popped.forEach((count) => count.classList.add("pop"));
    }
    lastColumn = nextColumn;
    lastCounts = nextCounts;
  }

  // ---------- drag and drop: pointer events so a finger works too (hold a moment on touch, so scrolling still scrolls) ----------

  interface Drag {
    ghost: HTMLElement;
    offsetX: number;
    offsetY: number;
    hot: HTMLElement | null;
    x: number;
    frame: number;
  }

  function attachDrag(tile: HTMLElement, card: KanbanCard<T>, from: KanbanColumn): void {
    let pointer = -1;
    let startX = 0;
    let startY = 0;
    let timer = 0;
    let drag: Drag | null = null;

    const hotColumn = (x: number, y: number): HTMLElement | null => document.elementFromPoint(x, y)?.closest<HTMLElement>(".task-col") ?? null;

    const begin = (event: PointerEvent) => {
      window.clearTimeout(timer);
      const rect = tile.getBoundingClientRect();
      const ghost = tile.cloneNode(true) as HTMLElement;
      ghost.classList.add("task-ghost");
      ghost.removeAttribute("data-key");
      ghost.style.width = `${rect.width}px`;
      ghost.style.left = `${rect.left}px`;
      ghost.style.top = `${rect.top}px`;
      // the ghost lives on the body, so it brings its column colour along
      if (from.color) ghost.style.setProperty("--st", from.color);
      document.body.append(ghost);
      drag = { ghost, offsetX: event.clientX - rect.left, offsetY: event.clientY - rect.top, hot: null, x: event.clientX, frame: 0 };
      isDragging = true;
      tile.classList.add("drag-origin");
      host.classList.add("dragging");
      try {
        tile.setPointerCapture(pointer);
      } catch {
        // the pointer may be gone already
      }
      buzz(8);
      const scroll = () => {
        if (!drag) return;
        const box = host.getBoundingClientRect();
        if (drag.x < box.left + 48) host.scrollLeft -= 14;
        else if (drag.x > box.right - 48) host.scrollLeft += 14;
        drag.frame = requestAnimationFrame(scroll);
      };
      drag.frame = requestAnimationFrame(scroll);
    };

    const move = (event: PointerEvent) => {
      if (!drag) return;
      drag.x = event.clientX;
      drag.ghost.style.transform = `translate(${event.clientX - drag.offsetX - parseFloat(drag.ghost.style.left)}px, ${event.clientY - drag.offsetY - parseFloat(drag.ghost.style.top)}px) rotate(-2deg) scale(1.03)`;
      const hot = hotColumn(event.clientX, event.clientY);
      if (hot !== drag.hot) {
        drag.hot?.classList.remove("drop-hot");
        hot?.classList.add("drop-hot");
        drag.hot = hot;
        if (hot) buzz(4);
      }
    };

    const finish = (event: PointerEvent, commit: boolean) => {
      window.clearTimeout(timer);
      pointer = -1;
      if (!drag) return;
      const current = drag;
      drag = null;
      cancelAnimationFrame(current.frame);
      current.hot?.classList.remove("drop-hot");
      host.classList.remove("dragging");
      suppressClick = performance.now();
      const target = commit ? hotColumn(event.clientX, event.clientY) : null;
      const back = () => {
        const rect = tile.getBoundingClientRect();
        current.ghost.classList.add("returning");
        current.ghost.style.transform = `translate(${rect.left - parseFloat(current.ghost.style.left)}px, ${rect.top - parseFloat(current.ghost.style.top)}px)`;
        window.setTimeout(() => {
          current.ghost.remove();
          tile.classList.remove("drag-origin");
          settle();
        }, calmMotion() ? 0 : 220);
      };
      const to = target?.dataset.status;
      if (to && columnIds.has(to) && to !== from.id) {
        // the card slides on from where it was let go
        dropRects.set(card.key, current.ghost.getBoundingClientRect());
        current.ghost.remove();
        settle();
        options.move(card, to);
      } else if (to === "trash") {
        const mode = trashMode(card);
        if (mode === "arm") {
          // deleting for good takes the same second tap as the button
          back();
          tile.querySelector(".task-trash")?.dispatchEvent(new Event("task-arm"));
          flashToast(t("tap the trash can on the card to delete it"));
        } else if (mode === "now") {
          current.ghost.remove();
          tile.classList.remove("drag-origin");
          settle();
          options.trash(card, tile);
        } else back();
      } else back();
    };

    tile.addEventListener("pointerdown", (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      if ((event.target as Element).closest("button")) return;
      pointer = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      if (event.pointerType === "touch") timer = window.setTimeout(() => begin(event), 330);
    });
    tile.addEventListener("pointermove", (event) => {
      if (event.pointerId !== pointer) return;
      if (drag) {
        event.preventDefault();
        move(event);
        return;
      }
      const far = Math.hypot(event.clientX - startX, event.clientY - startY);
      if (event.pointerType === "touch") {
        if (far > 8) {
          window.clearTimeout(timer);
          pointer = -1;
        }
      } else if (far > 5) {
        begin(event);
        move(event);
      }
    });
    tile.addEventListener("pointerup", (event) => {
      if (event.pointerId === pointer) finish(event, true);
    });
    tile.addEventListener("pointercancel", (event) => {
      if (event.pointerId === pointer) finish(event, false);
    });
    // once the hold has picked a card up, the finger must not scroll the board
    tile.addEventListener("touchmove", (event) => {
      if (drag) event.preventDefault();
    }, { passive: false });
    tile.addEventListener("contextmenu", (event) => {
      if (drag || pointer !== -1) event.preventDefault();
    });
    // a drop is not a tap on the card
    tile.addEventListener("click", (event) => {
      if (performance.now() - suppressClick < 400) event.stopImmediatePropagation();
    }, true);
  }

  return { paint, dragging: () => isDragging };
}
